import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import BaseDetails from 'containers/Base/BaseDetails';
import staticText from 'static/text_display.json';
import { translateValue, translatedKeyValueText } from 'utils/methods/helpers';
import { closeDetails, toggleModal, openDetails } from 'reducers/ui';
import { PATROL_STATUS } from 'utils/constants/patrol';
import {
  patrolDataGridActions,
  patrolTasksNS,
  patrolTaskSelectors,
  patrolPageActions,
} from 'containers/Pages/Tasks/Patrols';
import { actions as patrolTemplateActions } from 'containers/Pages/Tasks/PatrolTemplates/Modules/dataGridTable';
import isEmpty from 'lodash/isEmpty';
import { getUserId } from 'reducers/users';
import Operational from './operationalComponent';

const BasePatrolTasksDetails = ({
  id,
  cancel,
  downloadable = true,
  toggleEditMode,
}) => {
  //Actions-----------------------------
  const userID = useSelector(state => getUserId(state));
  const loading = useSelector(state => state.ui.loading);
  const dispatch = useDispatch();
  const dispatchFetchPatrolReportDetails = useCallback(
    id => {
      dispatch(patrolDataGridActions.fetchGridRowDetails({ id }));
    },
    [dispatch],
  );
  const dispatchToggleModal = useCallback(() => {
    dispatch(toggleModal(patrolTasksNS));
  }, [dispatch]);

  const dispatchClearPatrolTaskDetails = useCallback(() => {
    dispatch(patrolDataGridActions.setGridRowDetails({ details: {} }));
  }, [dispatch]);
  const dispatchClearPatrolTempleteDetails = useCallback(() => {
    dispatch(patrolTemplateActions.setGridRowDetails({ details: {} }));
  }, [dispatch]);

  const dispatchFetchPatrolDetailsPdf = useCallback(
    id => {
      dispatch(patrolPageActions.printDetails({ id }));
    },
    [dispatch],
  );
  const dispatchCloseDetailPanel = useCallback(
    patrolTasksNS => {
      dispatch(closeDetails(patrolTasksNS));
    },
    [dispatch],
  );

  // -------------------------------------

  //Selectors------------------------------
  const patrolDetails = useSelector(state =>
    patrolTaskSelectors.getPatrolTaskDetailsTransformed(state),
  );
  //---------------------------------------

  const onDownloadClick = () => {
    dispatchFetchPatrolDetailsPdf(id);
  };

  useEffect(() => {
    dispatchFetchPatrolReportDetails(id);
    dispatch(openDetails(patrolTasksNS));
    return () => {
      dispatchClearPatrolTaskDetails();
      dispatchClearPatrolTempleteDetails();
      dispatchCloseDetailPanel(patrolTasksNS);
    };
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const edit = () => {
    toggleEditMode();
    dispatchToggleModal();
  };

  const checkIfEditable = patrolDetails => {
    if (isEmpty(patrolDetails)) return false;
    // if the userID is other than the user of this account, then disable it
    if (patrolDetails.others.patrollerID.value !== userID) {
      return false;
    }

    return (
      patrolDetails.others?.proximityAlertDisabled.value &&
      patrolDetails?.others?.currentStatus?.value ===
        PATROL_STATUS.IN_PROGRESS.id
    );
  };

  return (
    <BaseDetails
      operationComponent={<Operational />}
      editable={checkIfEditable(patrolDetails)}
      header={translateValue('patrol_details_title')}
      details={patrolDetails}
      cancel={cancel}
      downloadable={downloadable}
      onEdit={edit}
      loading={loading}
      editHoverText={translatedKeyValueText('edit_value_title', {
        value: !isEmpty(patrolDetails)
          ? patrolDetails.generalInfo.patrolName.value
          : staticText.managerial_view.patrol.edit_default,
      })}
      {...(downloadable && {
        onDownloadClick: onDownloadClick,
        downloadHoverText: translatedKeyValueText(
          'download_qrcode_operational',
          {
            value: !isEmpty(patrolDetails)
              ? patrolDetails.generalInfo.patrolName.value
              : staticText.managerial_view.patrol.edit_default,
          },
        ),
      })}
    />
  );
};
BasePatrolTasksDetails.propTypes = {
  id: PropTypes.string,
  cancel: PropTypes.func,
  downloadable: PropTypes.bool,

  toggleEditMode: PropTypes.func,
};

export default BasePatrolTasksDetails;
